import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, FileSpreadsheet, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAccounts } from "@/features/accounts";
import {
  useGeneralLedger,
  type LedgerTransaction,
} from "@/features/general-ledger";

/**
 * Get the first day of the current month.
 */
function getFirstDayOfMonth(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0] as string;
}

/**
 * Get today's date.
 */
function getToday(): string {
  return new Date().toISOString().split("T")[0] as string;
}

/**
 * Format a date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format a number as currency.
 */
function formatCurrency(value: string | null): string {
  if (!value) return "";
  const num = parseFloat(value);
  return num.toLocaleString("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Account type labels in Spanish.
 */
const accountTypeLabels: Record<string, string> = {
  ASSET: "Activo",
  LIABILITY: "Pasivo",
  EQUITY: "Patrimonio",
  REVENUE: "Ingreso",
  EXPENSE: "Gasto",
};

/**
 * Normal balance labels in Spanish.
 */
const normalBalanceLabels: Record<string, string> = {
  DEBIT: "Debito",
  CREDIT: "Credito",
};

/**
 * General Ledger page (Mayor General).
 *
 * Shows account-centric view of journal entries with running balances.
 * User selects an account and date range to view the ledger.
 */
export function MayorGeneralPage() {
  const navigate = useNavigate();

  // Account selector state
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [accountSearch, setAccountSearch] = useState("");

  // Date range state
  const [startDate, setStartDate] = useState(getFirstDayOfMonth);
  const [endDate, setEndDate] = useState(getToday);

  // Fetch all accounts for the selector
  const { accounts, isLoading: accountsLoading } = useAccounts();

  // Filter to only postable (non-header) accounts and apply search
  const postableAccounts = useMemo(() => {
    return accounts
      .filter((a) => !a.isHeader)
      .filter((a) => {
        if (!accountSearch) return true;
        const search = accountSearch.toLowerCase();
        return (
          a.code.toLowerCase().includes(search) ||
          a.name.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [accounts, accountSearch]);

  // Fetch ledger data when account and dates are selected
  const {
    ledger,
    isLoading: ledgerLoading,
    error: ledgerError,
  } = useGeneralLedger({
    accountId: selectedAccountId || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  // Navigate to journal entry when clicking on a transaction
  const handleTransactionClick = (tx: LedgerTransaction) => {
    navigate(`/contabilidad/diario/${tx.entryId}`);
  };

  // Export to Excel placeholder
  const handleExport = () => {
    // TODO: Implement Excel export
    alert("Exportar a Excel - Proximamente");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Mayor General</h1>
          <p className="text-sm text-muted-foreground">
            Vista de movimientos por cuenta
          </p>
        </div>
        {ledger && (
          <Button variant="outline" onClick={handleExport}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Exportar
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Account selector */}
        <div className="flex-1 min-w-[300px]">
          <label className="text-sm font-medium mb-1 block">Cuenta</label>
          <Select
            value={selectedAccountId}
            onValueChange={setSelectedAccountId}
            disabled={accountsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione una cuenta..." />
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <Input
                  placeholder="Buscar cuenta..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="h-8"
                />
              </div>
              {postableAccounts.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground text-center">
                  No se encontraron cuentas
                </div>
              ) : (
                postableAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.code} - {account.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Date range */}
        <div className="flex gap-2">
          <div>
            <label className="text-sm font-medium mb-1 block">Desde</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Hasta</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {!selectedAccountId ? (
        // No account selected state
        <div className="border rounded-lg p-12 bg-card flex flex-col items-center justify-center text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Seleccione una cuenta</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Elija una cuenta del catalogo para ver sus movimientos y saldo
            durante el periodo seleccionado.
          </p>
        </div>
      ) : ledgerLoading ? (
        // Loading state
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : ledgerError ? (
        // Error state
        <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
          <AlertCircle className="h-8 w-8" />
          <p>Error al cargar el mayor</p>
          <p className="text-sm text-muted-foreground">{ledgerError.message}</p>
        </div>
      ) : ledger ? (
        // Ledger view
        <div className="border rounded-lg bg-card overflow-hidden">
          {/* Account header */}
          <div className="bg-muted px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  {ledger.account.code} - {ledger.account.name}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {accountTypeLabels[ledger.account.accountType]} | Saldo
                  Normal: {normalBalanceLabels[ledger.account.normalBalance]}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {formatDate(ledger.period.startDate)} -{" "}
                  {formatDate(ledger.period.endDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Opening balance */}
          <div className="px-4 py-2 bg-muted/50 border-b flex justify-between items-center">
            <span className="text-sm font-medium">Saldo Inicial</span>
            <span className="text-sm font-mono">
              ${formatCurrency(ledger.openingBalance)}
            </span>
          </div>

          {/* Transactions table */}
          {ledger.transactions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No hay movimientos en el periodo seleccionado
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground bg-muted/30">
                  <th className="px-4 py-2 font-medium w-24">Fecha</th>
                  <th className="px-4 py-2 font-medium w-24">Asiento</th>
                  <th className="px-4 py-2 font-medium">Descripcion</th>
                  <th className="px-4 py-2 font-medium text-right w-32">
                    Debito
                  </th>
                  <th className="px-4 py-2 font-medium text-right w-32">
                    Credito
                  </th>
                  <th className="px-4 py-2 font-medium text-right w-36">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {ledger.transactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleTransactionClick(tx)}
                  >
                    <td className="px-4 py-2 text-sm">
                      {formatDate(tx.transactionDate)}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-primary">
                      JE-{String(tx.entryNumber).padStart(6, "0")}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {tx.description}
                      {tx.reference && (
                        <span className="text-muted-foreground ml-2">
                          ({tx.reference})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono">
                      {tx.debit ? `$${formatCurrency(tx.debit)}` : ""}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono">
                      {tx.credit ? `$${formatCurrency(tx.credit)}` : ""}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono">
                      ${formatCurrency(tx.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Period totals and closing balance */}
          <div className="border-t bg-muted/50">
            <div className="px-4 py-2 flex justify-between items-center border-b">
              <span className="text-sm font-medium">Totales del Periodo</span>
              <div className="flex gap-8">
                <span className="text-sm font-mono">
                  Debitos: ${formatCurrency(ledger.periodDebits)}
                </span>
                <span className="text-sm font-mono">
                  Creditos: ${formatCurrency(ledger.periodCredits)}
                </span>
              </div>
            </div>
            <div className="px-4 py-2 flex justify-between items-center">
              <span className="text-sm font-semibold">Saldo Final</span>
              <span
                className={cn(
                  "text-lg font-mono font-semibold",
                  parseFloat(ledger.closingBalance) < 0
                    ? "text-destructive"
                    : "",
                )}
              >
                ${formatCurrency(ledger.closingBalance)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
